<template>
  <Teleport to="body">
    <Transition name="rm" @after-enter="focusIn" @after-leave="restoreFocus">
      <div
        v-if="open"
        ref="panelRef"
        data-map-inset="highlights"
        role="dialog"
        aria-modal="false"
        :aria-label="title ?? ariaLabel"
        tabindex="-1"
        data-rm-sheet
        :class="[
          'raw-glass fixed inset-0 top-auto z-20 flex max-h-[calc(100dvh-var(--raw-header-height)-var(--raw-gappers))] flex-col gap-8 rounded-t-[1.5rem] px-[--raw-panel-space] py-8 md:bottom-auto md:left-auto md:right-[var(--raw-edge-space)] md:top-[var(--raw-header-offset)] md:h-auto md:max-h-[calc(100dvh-var(--raw-header-offset)-var(--raw-edge-space)-var(--raw-gappers)-var(--raw-zoom-control-height))] md:w-[var(--raw-tablet-panel-width)] md:rounded-[1.5rem] lg:w-[460px]',
          expanded
            ? 'h-[calc(100dvh-var(--raw-header-height)-var(--raw-gappers))]'
            : 'h-[calc(85dvh-var(--raw-header-height)-var(--raw-gappers))]',
        ]"
        :style="dragStyle"
      >
        <!-- Drag the handle to dismiss (down) or grow (up); mobile only. -->
        <div ref="handleEl" data-drag-handle class="md:hidden" />

        <!-- Host-framed (built-in header + scroll) when a title is given;
                     otherwise the slot frames itself (e.g. a detail view). -->
        <DetailLayout v-if="title" :title :icon @close="emit('close')">
          <slot />
        </DetailLayout>
        <slot v-else />
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { onKeyStroke } from '@vueuse/core'

import DetailLayout from '~/scopes/campaign/components/UI/DetailLayout.vue'
import { useIsPhone } from '~/scopes/campaign/composables/useIsPhone'
import { useModalState } from '~/scopes/campaign/composables/useModalState'
import { usePanelDrag } from '~/scopes/campaign/composables/usePanelDrag'
import { useSheetExpansion } from '~/scopes/campaign/composables/useSheetExpansion'

const props = defineProps<{
  /** Whether the panel is shown. Open/close is owned by the caller (router-driven). */
  open: boolean
  /** When set, the panel frames the slot with a built-in header + scroll. Otherwise the slot frames itself (e.g. a detail view with its own header + scroll). */
  title?: string
  /** Optional leading icon (asset URL) shown beside the built-in `title`. Ignored when the slot frames its own header. */
  icon?: string
  /** Accessible name used when there is no `title` (e.g. a detail view whose slotted content carries its own header). */
  ariaLabel?: string
  /** Open at full height rather than the default (a detail on phone); still draggable back down. */
  fullHeight?: boolean
  /** Marks the `?metal=` quick-view instance: layered above route panels, it keeps Escape while they yield (see the Escape handler). */
  layered?: boolean
}>()

// `via` tells the caller *why* we are closing: a drag means the user pushed the whole
// sheet away, which a two-level caller may answer differently than its ✕.
const emit = defineEmits<{ close: [via?: 'drag'] }>()

const route = useRoute()
const { activeCinema, activeView, quickMetal } = useModalState()

const panelRef = ref<HTMLElement | null>(null)
// Element focused right before the panel opened, so we can restore focus on close.
const opener = ref<HTMLElement | null>(null)

// Drag is phone-only (<768), where the sheet opens to a partial height (the map
// peeks above it) and the handle drags up to grow it or down to dismiss (reusing
// the close path). At 768 and up the modal is the right rail — the handle is
// `md:hidden`, so the gesture never fires.
const handleEl = ref<HTMLElement | null>(null)
const isPhone = useIsPhone()
const dragEnabled = computed(() => props.open && isPhone.value)

// Two-level rest size: full when the slot asks for it, else the default. Quick views
// close on a downward drag instead of stepping to the default (`twoStage` off).
const { expanded } = useSheetExpansion(
  dragEnabled,
  computed(() => props.fullHeight),
)

const { height: dragHeight, dragging } = usePanelDrag(panelRef, handleEl, {
  enabled: dragEnabled,
  expanded,
  twoStage: !props.layered,
  onDismiss: startDismiss,
})

// Drag-dismiss: clearing the drag height would snap the sheet back to full size and
// pop it out, so we shrink it to 0 and close on the way down — the leave transition
// then plays on an already-flat box.
const dismissing = ref(false)

function startDismiss() {
  if (dismissing.value) return
  dismissing.value = true
  emit('close', 'drag')
}

// Re-grabbing the handle mid-shrink cancels the pending dismiss.
watch(dragging, (isDragging) => {
  if (isDragging) dismissing.value = false
})

// A two-level caller may answer `close` without unmounting us, and no leave
// transition would then clear the shrink — post-flush, so `open` has settled.
watch(
  () => route.fullPath,
  () => {
    if (props.open) dismissing.value = false
  },
  { flush: 'post' },
)

// Override the height only while dragging (track the finger) or while shrinking to
// dismiss. At rest we return `{}`, so the resting-height class owns the size and
// `.rm-sheet` animates the snap while the enter/leave (`.rm-*`) transitions own
// appear/exit — keeping an inline `transition` here would swallow those, so an
// expanded sheet closed via the ✕/map would pop out instead of sliding down.
const dragStyle = computed(() => {
  if (dragging.value)
    return {
      height: dragHeight.value !== null ? `${dragHeight.value}px` : undefined,
      transition: 'none',
    }
  if (dismissing.value)
    // Collapse the vertical padding too: with border-box, height:0 still
    // leaves the panel's ~24px of padding visible, so it stops short and
    // pops out. Animating the padding to 0 lets it shrink to a true zero.
    return {
      height: '0px',
      paddingTop: '0px',
      paddingBottom: '0px',
      overflow: 'hidden',
      transition:
        'height var(--raw-out-quart), padding-top var(--raw-out-quart), padding-bottom var(--raw-out-quart)',
    }
  return {}
})

watch(
  () => props.open,
  (isOpen, wasOpen) => {
    if (isOpen && !wasOpen)
      opener.value = (document?.activeElement as HTMLElement | null) ?? null
  },
)

function focusIn() {
  nextTick(() => {
    const root = panelRef.value
    if (!root) return
    const target =
      root.querySelector<HTMLElement>(
        'button, [href], input, [tabindex]:not([tabindex="-1"])',
      ) ?? root
    target.focus()
  })
}

function restoreFocus() {
  const el = opener.value
  if (el?.isConnected) el.focus()
  opener.value = null
  // The sheet is gone — clear the shrink so it reopens at its resting height.
  dismissing.value = false
}

// Non-modal dialog: Escape closes, but we never trap focus or lock scroll —
// the map behind the panel must stay interactive. Escape goes to the topmost
// layer only: cinema mode first, then the `?view=` tier as a whole —
// whichever card it is, which is what keeps this line unchanged when one is added — then the
// `?metal=` quick view; a route panel underneath would otherwise close too and navigate away
// with the layered state. Not a defensive clause: take action layers on the metal panels this
// component renders, and a footer document opens over whatever panel is on screen. And since
// `activeView` is validated, an unknown value renders nothing *and* swallows nothing here.
onKeyStroke('Escape', (event) => {
  if (!props.open || activeCinema.value) return
  if (activeView.value) return
  if (quickMetal.value && !props.layered) return
  event.preventDefault()
  emit('close')
})
</script>

<style scoped>
/* Animates the drag snap between the default and full-height rest levels. Declared
   first so the enter/leave transitions below win while the sheet appears/exits
   (height holds steady there — they only move transform + opacity). */
[data-rm-sheet] {
  transition: height var(--raw-out-quart);
}
.rm-enter-active,
.rm-leave-active {
  transition:
    transform var(--raw-in-out-quart),
    opacity var(--raw-in-out-quart);
}
.rm-enter-from,
.rm-leave-to {
  transform: translateY(10%);
  opacity: 0;

  /* Rail tiers: the modal is the right rail at every width, so it slides in from
       the right edge rather than up from the bottom sheet. */
  @media (min-width: 768px) {
    transform: translateX(10%);
  }
}

@media (prefers-reduced-motion: reduce) {
  [data-rm-sheet] {
    transition: none;
  }
  .rm-enter-active,
  .rm-leave-active {
    transition: opacity 0.15s linear;
  }
  .rm-enter-from,
  .rm-leave-to {
    transform: none;
  }
}
</style>
